import {
    GuestCount,
    Reservation,
    TableReservationEvent
} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {Profile} from "crqlar-react-common/dist/models/profile/profile.model";
import {
    ProfileActiveStay
} from "crqlar-react-common/dist/models/profile/profileActiveStay.model";
import {AgeCategory} from "crqlar-react-common/dist/models/ageCategory/ageCategory.model";
import {InternalNode} from "crqlar-react-common/dist/models/profile/internalNode.model";
import {endOfDay, startOfDay} from "crqlar-react-common/dist/utils/date.util";
import {mapGuestCountsToServiceAgeCategories} from "../../../common/ageCategoryDefaults";

/**
 * Everything the reservation dialog can derive from ONE guest instead of from the whole
 * reservation group. A group booked into several rooms shares neither its stay window nor its
 * guest mix: rooms 22/24 can depart on the 23rd while rooms 61/84 stay until the 24th. Whenever a
 * profile is picked in the dialog's guest list the form is recalculated from that guest's own
 * accommodation reservation, so the serial (multiday) series ends on THAT guest's check-out date.
 */
export interface ProfileStayContext {
    profileId: string
    /** The guest's own accommodation reservation - the source of every value below. */
    accommodationReservation?: Reservation
    checkInDate?: Date
    checkOutDate?: Date
    /** Counts of the guest's own room, still in accommodation age categories. */
    guestCounts?: GuestCount[]
    /** Restaurant the guest already eats at, derived from their existing restaurant reservations. */
    restaurantId?: string
    notes?: InternalNode[]
    tableReservationEvent?: TableReservationEvent
}

export const getStayStartDate = (reservation?: Reservation): Date | undefined => {
    const value = reservation?.actualStartDate ?? reservation?.expectedStartDate
    return value ? new Date(value) : undefined
}

export const getStayEndDate = (reservation?: Reservation): Date | undefined => {
    const value = reservation?.actualEndDate ?? reservation?.expectedEndDate
    return value ? new Date(value) : undefined
}

/**
 * Every stay the profile payload carries, de-duplicated by reservation. currentStay and
 * checkingInToday/checkingOutToday overlap by design, so the order here decides which record wins
 * when the same stay appears twice.
 */
const collectStays = (profile?: Profile): ProfileActiveStay[] => {
    const stays = [
        profile?.currentStay,
        profile?.checkingInToday,
        profile?.checkingOutToday,
        ...(profile?.upcomingStays ?? []),
        ...(profile?.activeStays ?? []),
        ...(profile?.pastStays ?? []),
    ].filter(Boolean) as ProfileActiveStay[]

    const seen = new Set<string>()
    return stays.filter(stay => {
        const key = stay.reservationId ?? [stay.startDay, stay.endDay, stay.roomId].join("-")
        if (seen.has(key)) return false
        seen.add(key)
        return true
    })
}

const toDayString = (date: Date): string => {
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")
    return [date.getFullYear(), month, day].join("-")
}

/**
 * The stay the dialog is being opened for: the one covering referenceDate (the day of the
 * reservation being created), falling back to the stay the guest is on right now and then to the
 * next arrival. Day strings compare lexicographically - they are always yyyy-MM-dd.
 */
export const pickStayForDate = (
    profile?: Profile,
    referenceDate?: Date,
): ProfileActiveStay | undefined => {
    const stays = collectStays(profile)
    if (!stays.length) return undefined

    if (referenceDate) {
        const referenceDay = toDayString(referenceDate)
        const covering = stays.find(stay => (
            (stay.startDay ?? "") <= referenceDay && (stay.endDay ?? "") >= referenceDay
        ))
        if (covering) return covering
    }

    return profile?.currentStay ?? profile?.upcomingStays?.[0] ?? stays[0]
}

/**
 * Same choice as pickStayForDate, but over already fetched accommodation reservations - used when
 * the dialog holds the reservations themselves rather than the profile's stay summaries.
 */
export const pickAccommodationReservationForDate = (
    accommodationReservations: Reservation[] = [],
    referenceDate?: Date,
): Reservation | undefined => {
    if (!accommodationReservations.length) return undefined
    if (!referenceDate) return accommodationReservations[0]

    const dayStart = startOfDay(referenceDate).getTime()
    const dayEnd = endOfDay(referenceDate).getTime()

    return accommodationReservations.find(reservation => {
        const start = getStayStartDate(reservation)?.getTime()
        const end = getStayEndDate(reservation)?.getTime()
        if (start == null || end == null) return false
        return start <= dayEnd && end >= dayStart
    }) ?? accommodationReservations[0]
}

/**
 * The restaurant this guest actually eats at. Guests of the same group can be split across
 * restaurants, so the one carrying most of their existing sittings wins; ties go to the most
 * recent sitting.
 */
export const pickPreferredRestaurantId = (
    restaurantReservations: Reservation[] = [],
    restaurantIds: string[] = [],
): string | undefined => {
    if (!restaurantIds.length) return undefined

    const knownRestaurantIds = new Set(restaurantIds)
    const countsById = new Map<string, number>()
    const lastSeenById = new Map<string, number>()

    restaurantReservations.forEach(reservation => {
        const restaurantId = reservation.resourceIds?.find(id => knownRestaurantIds.has(id))
        if (!restaurantId) return

        countsById.set(restaurantId, (countsById.get(restaurantId) ?? 0) + 1)
        const startedAt = reservation.expectedStartDate
            ? new Date(reservation.expectedStartDate).getTime()
            : 0
        lastSeenById.set(restaurantId, Math.max(lastSeenById.get(restaurantId) ?? 0, startedAt))
    })

    return Array.from(countsById)
        .sort(([leftId, leftCount], [rightId, rightCount]) => (
            rightCount - leftCount
            || (lastSeenById.get(rightId) ?? 0) - (lastSeenById.get(leftId) ?? 0)
        ))[0]?.[0]
}

export interface ApplyProfileStayOptions {
    /** Age categories the picked restaurant service offers - counts are translated onto them. */
    serviceAgeCategories: AgeCategory[]
    allAgeCategories: AgeCategory[]
    /** Every restaurant of the sector, so a restaurant switch can drop the previous one. */
    restaurants: {id?: string}[]
    /** Tables of every restaurant, used to tell a manual table pick from the restaurant itself. */
    tables: {id?: string, parentId?: string}[]
}

/**
 * Rewrites a *new* reservation draft so it describes the picked guest and nobody else.
 *
 * Only fields the guest's own stay actually answers are touched: an empty stay never blanks out
 * what the user already sees. Tables the user picked by hand (floorplan drag, manual selection)
 * are left alone - the restaurant is only re-derived while no table has been chosen yet.
 */
export const applyProfileStayToDraft = (
    draft: Reservation,
    context: ProfileStayContext | undefined,
    options: ApplyProfileStayOptions,
): Reservation => {
    if (!context) return draft

    let next: Reservation = draft

    if (context.guestCounts?.length) {
        next = {
            ...next,
            guestCounts: mapGuestCountsToServiceAgeCategories(
                context.guestCounts,
                options.serviceAgeCategories,
                options.allAgeCategories,
            ) as GuestCount[],
        }
    }

    const restaurantIds = new Set(
        options.restaurants.map(restaurant => restaurant.id).filter(Boolean) as string[]
    )
    const tableIdsByParent = new Map(
        options.tables.flatMap(table => table.id ? [[table.id, table.parentId] as const] : [])
    )
    const selectedTableIds = (next.resourceIds ?? []).filter(id => tableIdsByParent.has(id))
    const currentRestaurantId = (next.resourceIds ?? []).find(id => restaurantIds.has(id))

    if (
        context.restaurantId
        && context.restaurantId !== currentRestaurantId
        && selectedTableIds.length === 0
    ) {
        next = {
            ...next,
            resourceIds: [
                context.restaurantId,
                ...(next.resourceIds ?? []).filter(id => !restaurantIds.has(id)),
            ],
        }
    }

    if (context.notes?.length) {
        next = {...next, notes: context.notes}
    }

    if (context.tableReservationEvent) {
        next = {...next, tableReservationEvent: context.tableReservationEvent}
    }

    return next
}
